import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Icon, Button, Badge } from "@chakra-ui/react";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { FaFileDownload, FaBook, FaVideo, FaCalculator, FaNewspaper, FaPodcast } from "react-icons/fa";

const FinanceResources: React.FC = () => {
  const resources = [
    { icon: FaFileDownload, title: "First Home Buyer Guide", desc: "Complete guide to buying your first property", category: "Property", color: "blue" },
    { icon: FaFileDownload, title: "Investment Property Strategy", desc: "Build wealth through property investment", category: "Investment", color: "green" },
    { icon: FaFileDownload, title: "SMSF Setup Checklist", desc: "Step-by-step guide to starting your SMSF", category: "SMSF", color: "purple" },
    { icon: FaFileDownload, title: "Refinancing Guide", desc: "When and how to refinance your loan", category: "Property", color: "blue" },
    { icon: FaFileDownload, title: "Tax Deduction Checklist", desc: "Maximize your property tax deductions", category: "Tax", color: "orange" },
    { icon: FaFileDownload, title: "Asset Finance Options", desc: "Compare different asset finance types", category: "Asset", color: "cyan" },
    { icon: FaVideo, title: "Property Finance 101 Video", desc: "15-minute video covering loan basics", category: "Education", color: "red" },
    { icon: FaVideo, title: "SMSF Webinar Recording", desc: "Expert insights on SMSF strategies", category: "SMSF", color: "purple" },
    { icon: FaBook, title: "Market Update Report", desc: "Latest property and finance market trends", category: "Market", color: "teal" },
  ];

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/finance/resources"
        fallbackTitle="Finance Resources"
        fallbackDescription="Educational resources and guides for finance and wealth management"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.7)" textTransform="uppercase" letterSpacing="wider">
              Finance Section
            </Text>
            <Heading size="2xl" fontWeight="bold">
              Finance Resources
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">Education and tools</Text>
          </VStack>
        </Container>
      </Box>

      {/* Content Section */}
      <Container maxW="container.xl" py={20} flex="1">
        <VStack spacing={16}>
          {/* Introduction */}
          <Box textAlign="center" maxW="3xl" mx="auto">
            <Heading size="xl" mb={4}>Free Financial Resources</Heading>
            <Text fontSize="lg" color="gray.600">
              Access our library of guides, calculators, videos, and tools to help you make informed financial decisions.
              All resources are free to download and use.
            </Text>
          </Box>

          {/* Resource Categories */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} w="full">
            <Box bg="blue.50" p={6} borderRadius="lg" textAlign="center" border="1px solid" borderColor="blue.200">
              <Icon as={FaFileDownload} boxSize={10} color="blue.600" mb={3} mx="auto" />
              <Heading size="sm" mb={2} color="blue.800">Downloadable Guides</Heading>
              <Text fontSize="sm" color="gray.600">PDF guides covering all aspects of finance</Text>
            </Box>
            <Box bg="green.50" p={6} borderRadius="lg" textAlign="center" border="1px solid" borderColor="green.200">
              <Icon as={FaCalculator} boxSize={10} color="green.600" mb={3} mx="auto" />
              <Heading size="sm" mb={2} color="green.800">Financial Calculators</Heading>
              <Text fontSize="sm" color="gray.600">Interactive tools for planning and budgeting</Text>
            </Box>
            <Box bg="purple.50" p={6} borderRadius="lg" textAlign="center" border="1px solid" borderColor="purple.200">
              <Icon as={FaVideo} boxSize={10} color="purple.600" mb={3} mx="auto" />
              <Heading size="sm" mb={2} color="purple.800">Video Tutorials</Heading>
              <Text fontSize="sm" color="gray.600">Expert-led educational video content</Text>
            </Box>
          </SimpleGrid>

          {/* Resource Grid */}
          <Box w="full">
            <Heading size="lg" textAlign="center" mb={8}>Browse All Resources</Heading>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {resources.map((resource, index) => (
                <Box
                  key={index}
                  bg="white"
                  p={6}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="gray.200"
                  shadow="md"
                  _hover={{ shadow: "xl", transform: "translateY(-4px)", transition: "all 0.3s" }}
                >
                  <Badge colorScheme={resource.color} mb={3}>{resource.category}</Badge>
                  <Icon as={resource.icon} boxSize={8} color={`${resource.color}.600`} mb={3} />
                  <Heading size="sm" mb={2}>{resource.title}</Heading>
                  <Text fontSize="sm" color="gray.600" mb={4}>{resource.desc}</Text>
                  <Button size="sm" colorScheme={resource.color} variant="outline" width="full">
                    Download
                  </Button>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

          {/* Additional Resources */}
          <Box w="full">
            <Heading size="lg" textAlign="center" mb={8}>More Learning Resources</Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
              <Box bg="gradient-to-br" bgGradient="linear(to-br, orange.400, red.400)" p={8} borderRadius="xl" color="white" textAlign="center">
                <Icon as={FaNewspaper} boxSize={12} mb={4} mx="auto" />
                <Heading size="md" mb={2}>Market Insights</Heading>
                <Text fontSize="sm" mb={4}>Weekly updates on property and finance markets</Text>
                <Button colorScheme="whiteAlpha" variant="outline" size="sm">Subscribe</Button>
              </Box>
              <Box bg="gradient-to-br" bgGradient="linear(to-br, purple.400, pink.400)" p={8} borderRadius="xl" color="white" textAlign="center">
                <Icon as={FaPodcast} boxSize={12} mb={4} mx="auto" />
                <Heading size="md" mb={2}>Finance Podcast</Heading>
                <Text fontSize="sm" mb={4}>Expert interviews and financial tips</Text>
                <Button colorScheme="whiteAlpha" variant="outline" size="sm">Listen Now</Button>
              </Box>
              <Box bg="gradient-to-br" bgGradient="linear(to-br, blue.400, cyan.400)" p={8} borderRadius="xl" color="white" textAlign="center">
                <Icon as={FaBook} boxSize={12} mb={4} mx="auto" />
                <Heading size="md" mb={2}>Knowledge Base</Heading>
                <Text fontSize="sm" mb={4}>Comprehensive articles and FAQs</Text>
                <Button colorScheme="whiteAlpha" variant="outline" size="sm">Browse</Button>
              </Box>
            </SimpleGrid>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceResources;
